import { useState } from 'react'
import { motion } from 'framer-motion'
import { Send, CheckCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { InnerHero } from '@/components/sections'
import { Prefooter, Footer } from '@/components/layout'
import { LocalizedLink } from '@/components/LocalizedLink'

interface FormState {
  nome: string
  cognome: string
  email: string
  telefono: string
  azienda: string
  dipendenti: string
  messaggio: string
  privacy: boolean
}

export function RegaliAziendaliPage() {
  const { t } = useTranslation('common')

  const [form, setForm] = useState<FormState>({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    azienda: '',
    dipendenti: '',
    messaggio: '',
    privacy: false,
  })
  const [submitted, setSubmitted] = useState(false)

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((s) => ({ ...s, [key]: value }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !form.nome.trim() ||
      !form.cognome.trim() ||
      !form.email.trim() ||
      !form.telefono.trim() ||
      !form.azienda.trim() ||
      !form.dipendenti.trim() ||
      !form.messaggio.trim() ||
      !form.privacy
    ) {
      return
    }
    setSubmitted(true)
  }

  return (
    <main>
      <InnerHero
        eyebrow={t('regaliAziendali.heroEyebrow')}
        title={t('regaliAziendali.heroTitle')}
        titleItalic={t('regaliAziendali.heroTitleItalic')}
        body={t('regaliAziendali.heroBody')}
        breadcrumbs={[
          { label: t('nav.home'), href: '/it' },
          { label: t('regaliAziendali.breadcrumb') },
        ]}
      />

      <section className="bg-cream py-20 md:py-28 overflow-hidden">
        <div className="nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left — pitch */}
            <motion.aside
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1 }}
            >
              <div className="lg:sticky lg:top-32">
                <span className="nashi-eyebrow">{t('regaliAziendali.pitchEyebrow')}</span>
                <h2
                  className="mt-6 font-display text-bruno"
                  style={{
                    fontSize: 'clamp(1.875rem, 3vw, 2.5rem)',
                    lineHeight: 1.05,
                    letterSpacing: '-0.015em',
                  }}
                >
                  <span className="block">{t('regaliAziendali.pitchTitle1')}</span>
                  <span className="block italic font-light text-argan-deep">
                    {t('regaliAziendali.pitchTitle2')}
                  </span>
                </h2>
                <p className="mt-6 text-bruno-soft leading-relaxed">
                  {t('regaliAziendali.pitchBody')}
                </p>

                <div className="mt-12 aspect-[4/3] overflow-hidden">
                  <img
                    src="/images/regali-aziendali-kit.jpg"
                    alt={t('regaliAziendali.pitchImageAlt')}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.aside>

            {/* Right — form */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 1, delay: 0.1 }}
            >
              {submitted ? (
                <div className="bg-cream-soft px-8 py-14 md:px-14 md:py-20 text-center">
                  <CheckCircle className="mx-auto h-10 w-10 text-argan-deep mb-6" strokeWidth={1.5} />
                  <span className="font-display italic text-argan-deep text-sm tracking-[0.32em] uppercase">
                    {t('regaliAziendali.thanksEyebrow')}
                  </span>
                  <h3
                    className="mt-6 font-display text-bruno"
                    style={{
                      fontSize: 'clamp(2rem, 3.5vw, 3rem)',
                      lineHeight: 1.05,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    <span className="block">{t('regaliAziendali.thanksTitle1')}</span>
                    <span className="block italic font-light text-argan-deep">
                      {t('regaliAziendali.thanksTitle2')}
                    </span>
                  </h3>
                  <p className="mt-6 max-w-md mx-auto text-bruno-soft leading-relaxed">
                    {t('regaliAziendali.thanksBody')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                        {t('regaliAziendali.labelFirstName')} <span className="text-argan-deep">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={form.nome}
                        onChange={(e) => update('nome', e.target.value)}
                        placeholder={t('regaliAziendali.placeholderFirstName')}
                        className="nashi-input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                        {t('regaliAziendali.labelLastName')} <span className="text-argan-deep">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={form.cognome}
                        onChange={(e) => update('cognome', e.target.value)}
                        placeholder={t('regaliAziendali.placeholderLastName')}
                        className="nashi-input w-full"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                        {t('regaliAziendali.labelEmail')} <span className="text-argan-deep">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        placeholder={t('regaliAziendali.placeholderEmail')}
                        className="nashi-input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                        {t('regaliAziendali.labelPhone')} <span className="text-argan-deep">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        value={form.telefono}
                        onChange={(e) => update('telefono', e.target.value)}
                        placeholder={t('regaliAziendali.placeholderPhone')}
                        className="nashi-input w-full"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                        {t('regaliAziendali.labelCompany')} <span className="text-argan-deep">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={form.azienda}
                        onChange={(e) => update('azienda', e.target.value)}
                        placeholder={t('regaliAziendali.placeholderCompany')}
                        className="nashi-input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                        {t('regaliAziendali.labelEmployees')} <span className="text-argan-deep">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={form.dipendenti}
                        onChange={(e) => update('dipendenti', e.target.value)}
                        placeholder={t('regaliAziendali.placeholderEmployees')}
                        className="nashi-input w-full"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                      {t('regaliAziendali.labelMessage')} <span className="text-argan-deep">*</span>
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={form.messaggio}
                      onChange={(e) => update('messaggio', e.target.value)}
                      placeholder={t('regaliAziendali.placeholderMessage')}
                      className="nashi-input w-full resize-y min-h-[120px]"
                    />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={form.privacy}
                      onChange={(e) => update('privacy', e.target.checked)}
                      className="mt-1 accent-bruno w-3.5 h-3.5 shrink-0"
                    />
                    <span className="text-[12px] leading-relaxed text-bruno-soft">
                      {t('regaliAziendali.privacyPrefix')}{' '}
                      <LocalizedLink
                        to="privacy"
                        className="underline underline-offset-2 hover:text-argan-deep transition-colors"
                      >
                        {t('footer.privacy')}
                      </LocalizedLink>{' '}
                      {t('regaliAziendali.privacySuffix')}
                    </span>
                  </label>

                  <div className="pt-4">
                    <button
                      type="submit"
                      className="group inline-flex items-center gap-3 bg-bruno text-cream font-medium uppercase tracking-[0.28em] text-[11px] px-10 py-4 hover:bg-argan-deep transition-colors"
                    >
                      {t('regaliAziendali.submit')}
                      <Send
                        className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5"
                        strokeWidth={1.75}
                      />
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
